import { useFrame, useThree } from '@react-three/fiber';
import gsap from 'gsap';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type Group,
  type PointLight,
  type Texture,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Raycaster,
  TextureLoader,
  Vector3,
} from 'three';

import { QuadCloud } from '0_canvas/StaticCloud/QuadCloud';
import { usePromiseStore } from 'state/promiseStore';
import type { GroupProps } from 'types/r3f';
import { publicAssets } from 'utils/gcs';
import { createNoise2D } from 'utils/noise';
import { textureImage } from 'utils/textureImage';

const CLOUD_INTRO_SPEED = 0.4;
const LIGHTNING_URL = publicAssets('/lightning.png');

const textureLoader = new TextureLoader();
const noise = createNoise2D();
const raycaster = new Raycaster();
const raySource = new Vector3();
const rayDirection = new Vector3(0, -1, 0);
const direction = new Vector3();

type CloudHandle = {
  position: Vector3;
  scale: number;
  opacity: number;
  userData: {
    startPosition: Vector3;
    finalPosition: Vector3;
    volume: number;
  };
  isLoaded: boolean;
};

export interface ThunderstormProps extends GroupProps {
  cloudsRadius: number;
  holeRadius: number;
  lightningAreaRadius: number;
  altitude: number;
  density?: number;
  baseCloudVolume?: number;
  verticalSpread?: number;
}

const Thunderstorm = forwardRef<Group, ThunderstormProps>(
  (
    {
      cloudsRadius,
      holeRadius,
      lightningAreaRadius,
      altitude,
      density = 0.05,
      baseCloudVolume = 18.5,
      verticalSpread = 8,
      ...props
    },
    ref
  ) => {
    const exec = usePromiseStore(s => s.exec);
    const scene = useThree(state => state.scene);
    const camera = useThree(state => state.camera);
    const groupRef = useRef<Group>(null!);
    const light1Ref = useRef<PointLight>(null!);
    const light2Ref = useRef<PointLight>(null!);
    const light3Ref = useRef<PointLight>(null!);
    const cloudsRef = useRef<CloudHandle[]>([]);
    const lightningsRef = useRef<Group>(null!);
    const tilesContainerRef = useRef(scene.getObjectByName('tilesContainer'));
    const introProgressRef = useRef(0);
    const [pointLights] = useState([light1Ref, light2Ref, light3Ref]);

    useImperativeHandle(ref, () => groupRef.current);

    const [lightningTexture, setLightningTexture] = useState<Texture | null>(
      null
    );

    useEffect(() => {
      exec(LIGHTNING_URL, () => textureLoader.loadAsync(LIGHTNING_URL)).then(
        tex => setLightningTexture(tex)
      );
    }, [exec]);

    const clouds = useMemo(() => {
      cloudsRef.current.length = 0;
      const acc = [];
      let count = 0;

      const normalBounds = new Vector3(24, 8, 24);
      const largeBounds = new Vector3(48, 8, 48);

      for (let i = 0; i < cloudsRadius * 2 * density; i++) {
        for (let j = 0; j < cloudsRadius * 2 * density; j++) {
          const xFactor = i / (cloudsRadius * 2 * density - 1);
          const zFactor = j / (cloudsRadius * 2 * density - 1);

          // Distance from the center
          const dx = xFactor - 0.5;
          const dz = zFactor - 0.5;
          const totalDistance = dx * dx + dz * dz;

          // Omit clouds outside the circle shape
          if (totalDistance > 0.25) continue;

          if (totalDistance > 0.2) {
            const chance = 1 - (totalDistance - 0.2) / 0.05;
            if (Math.random() > chance) continue;
          }

          const nx = xFactor * cloudsRadius * 2 * 0.05;
          const nz = zFactor * cloudsRadius * 2 * 0.05;
          const n = (noise(nx, nz) + 1) / 2;

          const x = dx * cloudsRadius * 2;
          const z = dz * cloudsRadius * 2;

          // Radius in the range of 0 to 1
          const radius = Math.sqrt(Math.pow(dx * 2, 2) + Math.pow(dz * 2, 2));

          // Chance that always equals 1 when outside the hole, 0-1 range otherwise
          let chance = radius * (cloudsRadius / holeRadius);
          chance = Math.max(0, Math.min(1, chance));

          // Make clouds inside the hole less frequent
          chance = Math.pow(chance, 4);

          // Lower noise values mean that there is a smaller chance of spawning a cloud
          chance *= n;

          if (Math.random() > chance) continue;

          let bounds = normalBounds;
          let volume = baseCloudVolume;
          let y = altitude + (n - 0.5) * verticalSpread;

          const isLarge = Math.random() < 0.1;
          const isHuge = Math.random() < 0.05;

          if (isHuge) {
            bounds = largeBounds;
            volume *= 3;
            y += 30;
          } else if (isLarge) {
            bounds = largeBounds;
            volume *= 2;
            y += 10;
          }

          const finalPosition = new Vector3(x, y, z);

          const offset = new Vector3(
            Math.random() * 2 - 1,
            Math.random() * 2 - 1,
            Math.random() * 2 - 1
          ).multiplyScalar(volume * 0.6);

          const startPosition = finalPosition.clone().add(offset);

          const cloudIdx = count;

          const cloud = (
            <QuadCloud
              key={`${i}:${j}`}
              ref={ref => {
                if (ref) cloudsRef.current[cloudIdx] = ref as CloudHandle;
              }}
              position={startPosition.clone()}
              opacity={0}
              userData={{ startPosition, finalPosition, volume }}
              segmentCount={4}
              bounds={bounds}
              volume={volume}
              baseLight={0.8}
              pointLights={pointLights}
              rainHeight={altitude * 1.4}
            />
          );

          acc.push(cloud);
          count++;
        }
      }

      cloudsRef.current = Array(count).fill(null);

      return acc;
    }, [
      cloudsRadius,
      holeRadius,
      altitude,
      density,
      baseCloudVolume,
      verticalSpread,
      pointLights,
    ]);

    useFrame((_, deltaTime) => {
      if (introProgressRef.current > 1) return;

      const animationProgress = 1 - Math.pow(1 - introProgressRef.current, 2);

      for (const cloud of cloudsRef.current) {
        if (!cloud || !cloud.isLoaded) return;

        cloud.position.lerpVectors(
          cloud.userData.startPosition,
          cloud.userData.finalPosition,
          animationProgress
        );
        cloud.opacity = animationProgress / 3;
      }

      introProgressRef.current += deltaTime * CLOUD_INTRO_SPEED;
    });

    useEffect(() => {
      if (!groupRef.current || !lightningTexture) return;
      if (!tilesContainerRef.current) {
        const obj = scene.getObjectByName('tilesContainer');
        if (!obj) return;
        tilesContainerRef.current = obj;
      }

      const timeoutIds: number[] = [];

      const strike = (lightningLight: PointLight, cloudLight?: PointLight) => {
        const timeoutId = setTimeout(() => {
          const idx = timeoutIds.indexOf(timeoutId);
          if (idx >= 0) {
            timeoutIds.splice(idx, 1);
          }
          strike(lightningLight, cloudLight);
        }, Math.random() * 2000) as unknown as number;

        if (!groupRef.current) return;

        let lightning: Mesh | null = null;

        if (cloudLight) {
          const cloudIdx = Math.trunc(
            Math.random() * groupRef.current.children.length
          );
          const cloudPos = groupRef.current.children[cloudIdx].position;

          cloudLight.position.copy(cloudPos);
          lightningLight.position.copy(
            new Vector3(cloudPos.x, altitude / 2, cloudPos.z)
          );

          raycaster.near = 1;
          raycaster.far = altitude * 2;

          raySource.set(cloudPos.x, altitude, cloudPos.z);
          raycaster.set(raySource, rayDirection);

          const intersections = raycaster.intersectObjects(
            tilesContainerRef.current!.children,
            true
          );

          const point =
            intersections.length > 0 ? intersections.at(-1)!.point : null;

          if (!point) return;

          const lightningHeight = altitude - point.y;
          const textureAspect =
            textureImage(lightningTexture).width /
            textureImage(lightningTexture).height;

          const geometry = new PlaneGeometry(
            lightningHeight * textureAspect,
            lightningHeight
          );
          const material = new MeshBasicMaterial({
            map: lightningTexture,
            transparent: true,
            opacity: 0,
            depthWrite: false,
          });
          const mesh = new Mesh(geometry, material);

          const lightningY = point.y + (altitude - point.y) / 2;
          mesh.position.set(cloudPos.x, lightningY, cloudPos.z);

          lightning = mesh;
          lightningsRef.current.add(mesh);
        } else {
          const angle = Math.random() * Math.PI * 2;
          const r =
            cloudsRadius + (lightningAreaRadius - cloudsRadius) * Math.random();
          const x = r * Math.cos(angle);
          const z = r * Math.sin(angle);

          lightningLight.position.copy(new Vector3(x, altitude / 2, z));
        }

        const tl = gsap.timeline();
        const repetitionCount = Math.trunc(Math.random() * 3);

        for (let i = 0; i < repetitionCount; i++) {
          const fadeInDuration = 0.03 + Math.random() * 0.17;
          const fadeOutDuration = 0.03 + Math.random() * 0.17;

          tl.to(
            lightningLight,
            {
              intensity: altitude * 100,
              duration: fadeInDuration,
            },
            i === 0 ? undefined : `>${(Math.random() * 0.3).toFixed(2)}`
          );

          // prettier-ignore
          lightning && tl.to(lightning.material, {
            opacity: 1,
            duration: fadeInDuration,
          }, '<');

          cloudLight &&
            tl.to(
              cloudLight,
              {
                intensity: 50,
                duration: fadeInDuration,
              },
              '<'
            );

          tl.to(lightningLight, {
            intensity: 0,
            duration: fadeOutDuration,
          });

          // prettier-ignore
          lightning && tl.to(lightning.material, {
            opacity: 0,
            duration: fadeOutDuration,
            onComplete() {
              lightningsRef.current?.remove(lightning);
            },
          }, '<');

          cloudLight &&
            tl.to(
              cloudLight,
              {
                intensity: 0,
                duration: fadeOutDuration,
              },
              '<'
            );
        }
      };

      strike(light2Ref.current, light1Ref.current);
      strike(light3Ref.current);

      return () => {
        timeoutIds.forEach(id => clearTimeout(id));
      };
    }, [scene, lightningTexture, cloudsRadius, lightningAreaRadius, altitude]);

    useFrame(() => {
      for (const child of lightningsRef.current.children) {
        const lightning = child as Mesh;
        direction.subVectors(camera.position, lightning.position);
        direction.y = 0;
        direction.normalize();
        const angle = Math.atan2(direction.x, direction.z);
        lightning.rotation.y = angle;
      }
    });

    return (
      <group ref={groupRef} {...props}>
        {clouds}
        <spotLight
          position={[0, 150, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          intensity={5}
          distance={250}
          color={0xefcdc4}
          angle={Math.PI * 0.4}
          penumbra={0.75}
          decay={0.1}
        />
        <ambientLight color={0xadd4e3} intensity={0.8} />
        <pointLight ref={light1Ref} intensity={0} distance={50} />
        <pointLight ref={light2Ref} intensity={0} distance={altitude * 1.6} />
        <pointLight ref={light3Ref} intensity={0} distance={altitude * 1.6} />
        <group ref={lightningsRef} />
      </group>
    );
  }
);

Thunderstorm.displayName = 'Thunderstorm';
export default Thunderstorm;
